import * as React from "react";
import { useSelector } from "react-redux";
import { createContext, useContext, useState } from "react";
import styled from "styled-components";

import { ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { View } from "@com.mgmtp.a12.client/client-core/lib/core/view";
import IExternalEnumerationProvider from "@com.mgmtp.a12.formengine/formengine-core/lib/back-end/services/external-enumeration-provider";
import { FormEngineViews } from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";
import {
	DefaultFormModelMap,
	DefaultWidgetMap,
	EnablementByRow,
	FormModelMap,
	WidgetMap
} from "@com.mgmtp.a12.formengine/formengine-core/lib/view";
import { EnablementByButtonName } from "@com.mgmtp.a12.formengine/formengine-core/lib/view/internal/configuration/engine-configuration";
import { FormModel } from "@com.mgmtp.a12.formengine/formengine-core";
import { AutocompleteProps, HTMLInputAdapter, TextLineStateless } from "@com.mgmtp.a12.widgets/widgets-core";
import { DropDownItem } from "@com.mgmtp.a12.widgets/widgets-core/lib/dropdown";

import { isContactData } from "../../contactData/types";
import { formatContact } from "../../../utilities/contacts";
import { selectAvailableClients } from "../../../utilities/selectors/modulfDataSelectors";

import { InvitationModalDescriptor } from "../descriptors";
import { isInvitationDocumentData } from "../types";

export const INVITE_MANUAL_APP_KEY = "MANUAL";

export const InvitationModal: View.ViewComponent = function InvitationModal(props) {
	const activityId = props.activityId;

	const activity = useSelector(ActivitySelectors.activityById(activityId));
	const contactDataHolder = useSelector(
		ActivitySelectors.dataHolderByDescriptor(activityId, { type: "contactData" })
	);
	const contactData = isContactData(contactDataHolder?.data) ? contactDataHolder.data : undefined;

	const descriptor = InvitationModalDescriptor.isInstance(activity?.descriptor) ? activity.descriptor : undefined;
	const sourceAppKeys = descriptor?.sourceAppKeys ?? "";

	const availableClients = useSelector(selectAvailableClients(descriptor?.applicationKey ?? ""));
	const appKeyList = React.useMemo(
		() =>
			[sourceAppKeys, INVITE_MANUAL_APP_KEY]
				.join(",")
				.split(",")
				.map(s => s.trim())
				.filter(Boolean),
		[sourceAppKeys]
	);
	const availableClientOptions = React.useMemo(
		() => Object.fromEntries(availableClients.filter(it => it.length > 0).map(client => [client, { de: client }])),
		[availableClients]
	);
	const invitationEmailOptions = React.useMemo(
		() =>
			Object.fromEntries(
				appKeyList
					.flatMap(appKey => contactData?.values[appKey] ?? [])
					.map(entry => [entry.value, { de: formatContact(entry.value, entry.label) }])
			),
		[appKeyList, contactData]
	);
	const externalEnumerationProvider: IExternalEnumerationProvider = React.useMemo(() => {
		return (source: string) => {
			if (source === "availableClients") {
				return availableClientOptions;
			}
			if (source === "expire_items") {
				return EXPIRE_ITEMS;
			}
			if (source !== "invitationEmails" || !contactData) {
				return {};
			}
			return invitationEmailOptions;
		};
	}, [sourceAppKeys, contactData, availableClientOptions, invitationEmailOptions]);

	const data = useSelector(ActivitySelectors.data(activityId));
	const invitationRoot = isInvitationDocumentData(data) ? data.document.root : undefined;
	const hiddenIfEnablement = (predicate: (status: string | undefined) => boolean) =>
		Object.fromEntries(
			(invitationRoot?.existingInvitations ?? []).map((value, index) => [
				`${index + 1}`,
				{ hidden: predicate(value.status) }
			])
		);

	const enablementByRow = {
		existingInvitations_Repeat: {
			revoke_invitation: hiddenIfEnablement(status => status !== "ACTIVE"),
			resend_invitation: hiddenIfEnablement(status => status !== "ACTIVE" && status !== "EXPIRED"),
			delete_invitation: hiddenIfEnablement(status => status === "ACTIVE"),
			open_process: hiddenIfEnablement(status => status !== "USED")
		}
	} satisfies EnablementByRow;

	const enablementByName = {
		kontakte_anzeigen: { hidden: invitationRoot?.newInvitations.contactsAvailable === false },
		send_invitations: {
			hidden:
				invitationRoot?.newInvitations?.recipients?.length === 0 ||
				invitationRoot?.newInvitations?.recipients === undefined
		}
	} satisfies EnablementByButtonName;

	return (
		<HasContactsContext.Provider value={{ hasContacts: invitationRoot?.newInvitations.contactsAvailable ?? false }}>
			<FormEngineViews.FormEngine
				{...props}
				externalEnumerationProvider={externalEnumerationProvider}
				enablements={{ byRow: enablementByRow, byButtonName: enablementByName }}
				formModelMap={customFormModelMap}
				widgetMap={customWidgetMap}
			/>
		</HasContactsContext.Provider>
	);
};

const EXPIRE_ITEMS = {
	HOURS_24: { de: "Ein Tag" },
	HOURS_48: { de: "Zwei Tage" },
	HOURS_72: { de: "Drei Tage" },
	DAYS_7: { de: "Eine Woche" },
	DAYS_14: { de: "Zwei Wochen" },
	DAYS_30: { de: "Ein Monat (30 Tage)" }
} satisfies Record<string, { de: string }>;

const HasContactsContext = createContext<{ hasContacts: boolean }>({ hasContacts: true });
const RecipientContext = createContext<{ isRecipient: boolean } | undefined>(undefined);

const TextLine = HTMLInputAdapter(TextLineStateless);
const VerticalCenterWrapper = styled.div`
	display: flex;
	align-items: center;
	height: 100%;
	width: 100%;
`;
const CustomAutoComplete: React.ComponentType<AutocompleteProps> = function (props) {
	const isRecipient = useContext(RecipientContext)?.isRecipient ?? false;
	const hasContacts = useContext(HasContactsContext).hasContacts;
	const value = typeof props.value === "string" ? props.value : ((props.value as DropDownItem)?.value ?? "");
	const [buffer, setBuffer] = useState<string>(value);
	if (isRecipient && !hasContacts) {
		return (
			<VerticalCenterWrapper>
				<TextLine
					{...props}
					value={buffer}
					onValueChange={setBuffer}
					onBlur={() => props.onValueChange?.(buffer)}
				/>
			</VerticalCenterWrapper>
		);
	}
	return (
		<VerticalCenterWrapper>
			<DefaultWidgetMap.Autocomplete {...props} enableClearButton={false} />
		</VerticalCenterWrapper>
	);
};

const customWidgetMap = {
	...DefaultWidgetMap,
	Autocomplete: CustomAutoComplete
} satisfies WidgetMap;

const CustomInlineRepeat: React.ComponentType<FormModelMap.FormModelComponentProps<FormModel.InlineRepeat>> = function (
	props
) {
	const isRecipient = !!props.modelElement.annotations?.find(
		it => it.name === "mf_runtime_invitation" && it.value === "recpient"
	);
	return (
		<RecipientContext value={{ isRecipient }}>
			<DefaultFormModelMap.InlineRepeat.component {...props} />
		</RecipientContext>
	);
};
const customFormModelMap = {
	...DefaultFormModelMap,
	InlineRepeat: { component: CustomInlineRepeat }
} satisfies FormModelMap;
